import { createTheme } from '@mui/material/styles';
import { colors } from './colors';
const borderRadius = '12px';
const borderRadiusLarge = '16px';
export const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: colors.primary,
            light: colors.primaryLight,
            dark: colors.primaryDark,
            contrastText: colors.textPrimary,
        },
        secondary: {
            main: colors.accent,
            light: colors.accentLight,
            dark: colors.accentDark,
            contrastText: colors.secondaryDark,
        },
        error: {
            main: colors.error,
        },
        warning: {
            main: colors.warning,
        },
        success: {
            main: colors.success,
        },
        info: {
            main: colors.info,
        },
        background: {
            default: colors.background,
            paper: colors.surface,
        },
        text: {
            primary: colors.textPrimary,
            secondary: colors.textSecondary,
            disabled: colors.textHint,
        },
        divider: colors.cardBorder,
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.background,
                    color: colors.textPrimary,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    borderRadius: borderRadiusLarge,
                    border: `1px solid ${colors.cardBorder}`,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    borderRadius: borderRadiusLarge,
                    border: `1px solid ${colors.cardBorder}`,
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadiusLarge,
                    border: `1px solid ${colors.cardBorder}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surfaceVariant,
                    '& .MuiTableCell-head': {
                        backgroundColor: colors.surfaceVariant,
                        color: colors.textPrimary,
                        fontWeight: 600,
                        fontSize: '14px',
                        borderBottom: `1px solid ${colors.cardBorder}`,
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    color: colors.textPrimary,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: colors.surfaceVariant,
                        cursor: 'pointer',
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.surfaceVariant,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: colors.secondaryLight,
                    },
                    '&.voteTarget': {
                        backgroundColor: `${colors.primary} !important`,
                        color: `${colors.textPrimary} !important`,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '16px 20px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    border: 'none',
                    borderBottom: `1px solid ${colors.cardBorder}`,
                    '&.role': {
                        color: colors.primary,
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                    borderRadius: borderRadius,
                    backgroundColor: colors.surface,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    padding: '16px 20px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: colors.surfaceVariant,
                    },
                    '&.timer': {
                        backgroundColor: colors.textPrimary,
                        color: colors.background,
                        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
                    },
                    '&.gameButtonState': {
                        backgroundColor: colors.primary,
                    },
                    '&.round': {
                        backgroundColor: colors.primary,
                        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
                    },
                    '&.disabled': {
                        background: 'transparent',
                        color: colors.textHint,
                        cursor: 'default',
                    },
                    '&.gameButtonState:hover': {
                        backgroundColor: colors.primaryDark,
                    },
                    '&.cancel': {
                        padding: '16px 24px',
                    },
                    '&.status': {
                        background: colors.background,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 0,
                    color: colors.textSecondary,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    color: colors.textPrimary,
                    fontFamily: 'Inter, Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    letterSpacing: '0.5px',
                },
                secondary: {
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    color: colors.primary,
                    fontFamily: 'Inter, Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: 1.4,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    padding: '12px 24px',
                    transition: 'all 0.2s ease',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                contained: {
                    backgroundColor: colors.primary,
                    color: colors.textPrimary,
                    '&:hover': {
                        backgroundColor: colors.primaryDark,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: colors.surfaceVariant,
                        color: colors.textHint,
                    },
                },
                outlined: {
                    borderColor: colors.primary,
                    color: colors.primary,
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                    },
                },
                text: {
                    color: colors.textPrimary,
                    '&:hover': {
                        backgroundColor: colors.surfaceVariant,
                    },
                    '&.fouls': {
                        color: colors.info,
                        fontWeight: 400,
                    },
                    '&.wakeUpButton': {
                        backgroundColor: colors.primary,
                        color: colors.textPrimary,
                        padding: '10px 28px',
                        borderRadius: borderRadius,
                        '&:hover': {
                            backgroundColor: colors.primaryDark,
                        },
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: colors.textSecondary,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: colors.surfaceVariant,
                        color: colors.textPrimary,
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.surfaceVariant,
                        borderRadius: borderRadius,
                        '& fieldset': {
                            borderColor: colors.cardBorder,
                        },
                        '&:hover fieldset': {
                            borderColor: colors.textHint,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: colors.textPrimary,
                    },
                    '& .MuiInputLabel-root': {
                        color: colors.textSecondary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: colors.primary,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surfaceVariant,
                    borderRadius: borderRadius,
                    '& fieldset': {
                        borderColor: colors.cardBorder,
                    },
                    '&:hover fieldset': {
                        borderColor: colors.textHint,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: colors.primary,
                        borderWidth: '2px',
                    },
                },
                input: {
                    color: colors.textPrimary,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surfaceVariant,
                    borderRadius: borderRadius,
                },
                icon: {
                    color: colors.textSecondary,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: colors.textSecondary,
                    '&.Mui-checked': {
                        color: colors.primary,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: colors.textSecondary,
                    '&.Mui-checked': {
                        color: colors.primary,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.primary,
                        '& + .MuiSwitch-track': {
                            backgroundColor: colors.primary,
                        },
                    },
                },
                track: {
                    backgroundColor: colors.textHint,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surfaceVariant,
                    color: colors.textPrimary,
                    borderRadius: '20px',
                    border: `1px solid ${colors.cardBorder}`,
                    fontWeight: 500,
                    '&.MuiChip-colorPrimary': {
                        backgroundColor: colors.primary,
                        borderColor: colors.primary,
                    },
                    '&.MuiChip-colorSecondary': {
                        backgroundColor: colors.accent,
                        color: colors.secondaryDark,
                        borderColor: colors.accent,
                    },
                    '&.MuiChip-colorInfo': {
                        backgroundColor: 'rgba(59, 130, 246, 0.15)',
                        color: colors.info,
                        borderColor: colors.info,
                    },
                    '&.MuiChip-colorSuccess': {
                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                        color: colors.success,
                        borderColor: colors.success,
                    },
                    '&.MuiChip-colorWarning': {
                        backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        color: colors.warning,
                        borderColor: colors.warning,
                    },
                    '&.MuiChip-colorError': {
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        color: colors.error,
                        borderColor: colors.error,
                    },
                },
                outlined: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius,
                },
                standardInfo: {
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    color: colors.info,
                    border: `1px solid ${colors.info}`,
                },
                standardSuccess: {
                    backgroundColor: 'rgba(34, 197, 94, 0.15)',
                    color: colors.success,
                    border: `1px solid ${colors.success}`,
                },
                standardWarning: {
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    color: colors.warning,
                    border: `1px solid ${colors.warning}`,
                },
                standardError: {
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    color: colors.error,
                    border: `1px solid ${colors.error}`,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    borderRadius: '20px',
                    border: `1px solid ${colors.cardBorder}`,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: colors.textPrimary,
                    fontWeight: 600,
                    fontSize: '20px',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    color: colors.textSecondary,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${colors.cardBorder}`,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: colors.textPrimary,
                    '&:hover': {
                        backgroundColor: colors.surfaceVariant,
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.surfaceVariant,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: colors.secondaryLight,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: colors.surfaceVariant,
                    color: colors.textPrimary,
                    fontSize: '12px',
                    border: `1px solid ${colors.cardBorder}`,
                    borderRadius: '8px',
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    '& .MuiSnackbarContent-root': {
                        backgroundColor: colors.surfaceVariant,
                        color: colors.textPrimary,
                        borderRadius: borderRadius,
                    },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surfaceVariant,
                    borderRadius: borderRadius,
                },
                bar: {
                    backgroundColor: colors.primary,
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: colors.primary,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: colors.cardBorder,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    borderBottom: `1px solid ${colors.cardBorder}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    borderRight: `1px solid ${colors.cardBorder}`,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: colors.primary,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: colors.textSecondary,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&.Mui-selected': {
                        color: colors.primary,
                    },
                },
            },
        },
        MuiPagination: {
            styleOverrides: {
                root: {
                    '& .MuiPaginationItem-root': {
                        color: colors.textPrimary,
                        '&:hover': {
                            backgroundColor: colors.surfaceVariant,
                        },
                        '&.Mui-selected': {
                            backgroundColor: colors.primary,
                            color: colors.textPrimary,
                        },
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: 'Inter, Roboto, sans-serif',
        h1: {
            color: colors.textPrimary,
            fontSize: '34px',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
        },
        h2: {
            color: colors.textPrimary,
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '-0.25px',
            lineHeight: 1.3,
        },
        h3: {
            color: colors.textPrimary,
            fontSize: '24px',
            fontWeight: 700,
            lineHeight: 1.3,
        },
        h4: {
            color: colors.textPrimary,
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            color: colors.textPrimary,
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h6: {
            color: colors.textPrimary,
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        subtitle1: {
            color: colors.textSecondary,
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1.5,
        },
        subtitle2: {
            color: colors.textSecondary,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        body1: {
            color: colors.textPrimary,
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1.5,
        },
        body2: {
            color: colors.textSecondary,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.3px',
        },
        caption: {
            color: colors.textHint,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.4,
        },
        overline: {
            color: colors.textHint,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
        },
    },
});
